import { FeedPostDto } from "@/api/posts/types";
import { getUsersHref, getUsersName } from "@/api/users/helpers";
import { UserLink } from "@/app/(components)/Application/User/UserLink";
import DynamicLinkWrapper from "@/app/(components)/Basic/DynamicLinkWrapper";
import { ImageTypeHandler } from "@/app/(components)/Basic/ImageTypeHandler";
import DefaultProfile from "@/assets/graphics/default-profile.svg";
import { extractDateAndTime, formatDateWithHours } from "@/shared/dates";
import { twMerge } from "tailwind-merge";
import { EndemicUserWrapper } from "@/app/(components)/Basic/EndemicUserWrapper";

export interface Props {
  feedPost: FeedPostDto;
  forceLightTheme?: boolean;
}

export default function FeedPostAuthor({ feedPost, forceLightTheme }: Props) {
  const { date, time } = extractDateAndTime(
    formatDateWithHours(feedPost.createdAt),
  );

  return (
    <div className="flex items-start gap-4 md:gap-x-3 w-full">
      <UserLink artist={feedPost.author} tooltipPosition={["right", "middle"]}>
        <div className="relative md:w-10 md:h-10 h-8 w-8 shrink-0">
          {feedPost.author.imageUrl ? (
            <ImageTypeHandler
              src={feedPost.author.imageUrl}
              placeholder={feedPost.author.imagePlaceholder}
              alt={getUsersName(feedPost.author) || "profile image"}
              className="rounded-full"
              fill
              sizes="100px"
            />
          ) : (
            <DefaultProfile />
          )}
        </div>
      </UserLink>
      <div className="flex md:flex-col overflow-hidden gap-1 w-full">
        <div className="flex flex-col md:flex-row md:gap-2 md:items-center items-start">
          <DynamicLinkWrapper
            wrapWithLink={!feedPost.author.hideProfile}
            href={getUsersHref(feedPost.author)}
          >
            <EndemicUserWrapper id={feedPost.author.id}>
              <div className="truncate">
                {getUsersName(feedPost.author, true)}
              </div>
            </EndemicUserWrapper>
          </DynamicLinkWrapper>
          <DynamicLinkWrapper
            wrapWithLink={!feedPost.author.hideProfile}
            href={getUsersHref(feedPost.author)}
          >
            {feedPost.author.displayName && (
              <div
                className={twMerge(
                  "text-endemicGray-700 md:text-base tracking-[-0.32px] endemic:opacity-80 text-sm",
                  !forceLightTheme && "endemic:text-primary",
                )}
              >
                @{feedPost.author.displayName}
              </div>
            )}
          </DynamicLinkWrapper>
        </div>
        <p
          className={twMerge(
            "md:text-body-4 text-endemicGray-700 flex-col items-end mr-3 md:text-xs flex md:flex-row ml-auto text-xs md:m-0 md:gap-1",
            !forceLightTheme && "endemic:text-secondary",
          )}
        >
          <span className="text-right">{date}</span>
          <span className="text-right">{time}</span>
        </p>
      </div>
    </div>
  );
}
